import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { updateAvailability } from '../../redux/doctorSlice';
import { cancelAllAppointments } from '../../redux/appointmentSlice';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../../redux/blockingSlice';
import { X, Copy } from 'lucide-react';
import CustomSelect from '../common/CustomSelect';
import { Plus, Edit, Trash2 } from 'lucide-react';

const AvailabilityTab = () => {
    const { selectedDoctor, loading } = useSelector((state) => state.doctor);
    const { trynavigate } = useSelector((state) => state.blocking);
    const doctorId = selectedDoctor?.doctorId;
    const dispatch = useDispatch();
    const { t } = useTranslation(['availabilitytab']);
    const alladdress = selectedDoctor?.address || [];
    const [availability, setAvailability] = useState([]);
    const [initialAvailability, setInitialAvailability] = useState([]);
    const language = (localStorage.getItem('i18nextLng') || 'en').split('-')[0];
    const [hasChanges, setHasChanges] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [tempBreakStart, setTempBreakStart] = useState({ hours: '00', minutes: '00' });
    const [tempBreakEnd, setTempBreakEnd] = useState({ hours: '00', minutes: '00' });
    const [timeIndex, setTimeIndex] = useState(null);
    const [brkIndex, setBrkIndex] = useState(null);
    const [tempTime, setTempTime] = useState({ hours: '00', minutes: '00' });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Replicate modal states
    const [showReplicateModal, setShowReplicateModal] = useState(false);
    const [replicateSourceDay, setReplicateSourceDay] = useState('');
    const [replicateTargetDays, setReplicateTargetDays] = useState([]);

    // Custom dropdown states
    const [openDropdown, setOpenDropdown] = useState(null);

    // Initialize availability data for all days
    useEffect(() => {
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const initialAvailability = days.map((day, index) => {
            const existing = selectedDoctor?.availability?.find(
                (item) => item.day.toLowerCase() === day
            );
            return existing
                ? {
                    day: existing.day.toLowerCase(),
                    start: existing.start,
                    end: existing.end,
                    isActive: existing.isActive,
                    breaks: Array.isArray(existing.breaks) ? [...existing.breaks] : [],
                    addressType: existing.addressType,
                }
                : {
                    day,
                    start: '',
                    end: '',
                    isActive: false,
                    breaks: [],
                    addressType: '',
                };
        });
        setAvailability(initialAvailability);
        setInitialAvailability(JSON.stringify(initialAvailability));
        setHasChanges(false);
    }, [selectedDoctor]);

    // Track changes to enable/disable Update button
    useEffect(() => {
        const changed = JSON.stringify(availability) !== initialAvailability;
        setHasChanges(changed);
        dispatch(restrictnavigate(changed));
    }, [availability, initialAvailability, dispatch]);

    // Cleanup: remove navigation block on unmount
    useEffect(() => {
        return () => {
            dispatch(restrictnavigate(false));
        };
    }, [dispatch]);

    // Handle navigation blocking — show confirmation when user tries to navigate away with unsaved changes
    useEffect(() => {
        if (trynavigate && hasChanges) {
            Swal.fire({
                title: t('availabilitytab.unsaved_changes_title', 'Unsaved Changes'),
                text: t('availabilitytab.unsaved_changes_text', 'You have unsaved changes. Do you want to discard them?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('availabilitytab.discard', 'Discard'),
                confirmButtonColor: '#d33',
                cancelButtonText: t('availabilitytab.stay', 'Stay'),
                cancelButtonColor: '#9ca3af',
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    // Discard — leave pendingNavigation in place so the parent
                    // consumer (Sidebar / DoctorProfile) finishes the user's
                    // original navigation without a re-click.
                    dispatch(restrictnavigate(false));
                    dispatch(tryingnavigate(false));
                } else {
                    // Stay — drop the captured destination so it doesn't fire later.
                    dispatch(tryingnavigate(false));
                    dispatch(clearPendingNavigation());
                }
            });
        }
    }, [trynavigate, hasChanges, dispatch, t]);

    const openTimeModal = (index, type) => {
        if (!availability[index].isActive) return;
        setTimeIndex(index);
        setModalType(type);
        setModalIsOpen(true);
        if (type === 'break') {
            setBrkIndex(null);
            let initialBreakStart = availability[index].start;
            if (availability[index].breaks.length > 0) {
                const maxEnd = availability[index].breaks.reduce((max, brk) => Math.max(max, timeToMinutes(brk.breakEnd)), 0);
                initialBreakStart = minutesToTime(maxEnd);
            }
            const [hours, minutes] = initialBreakStart.split(':');
            const ceilMinutes = ceilToNext15(timeToMinutes(initialBreakStart));
            const ceiledTime = minutesToTime(ceilMinutes);
            const [ceiledHours, ceiledMins] = ceiledTime.split(':');
            setTempBreakStart({ hours: ceiledHours, minutes: ceiledMins });
            setTempBreakEnd({ hours: ceiledHours, minutes: ceiledMins });
        } else {
            const time = type === 'start' ? availability[index].start : availability[index].end;
            if (time) {
                const [hours, minutes] = time.split(':');
                setTempTime({ hours, minutes });
            } else {
                // Set initial placeholder - will be corrected by useEffect
                setTempTime({ hours: '00', minutes: '00' });
            }
        }
        setErrors((prev) => ({ ...prev, [index]: {} }));
    };

    const addAvailabilityBreak = (index) => {
        if (!availability[index].isActive) return;
        if (availability[index].breaks.length >= 3) {
            toast.warn(t('availabilitytab.maxBreaksText'));
            return;
        }
        setTimeIndex(index);
        setModalType('break');
        setModalIsOpen(true);
        setBrkIndex(null);

        let initialBreakStart = availability[index].start;
        if (availability[index].breaks.length > 0) {
            const maxEnd = availability[index].breaks.reduce((max, brk) => Math.max(max, timeToMinutes(brk.breakEnd)), 0);
            initialBreakStart = minutesToTime(maxEnd);
        }
        const [hours, minutes] = initialBreakStart.split(':');
        const ceilMinutes = ceilToNext15(timeToMinutes(initialBreakStart));
        const ceiledTime = minutesToTime(ceilMinutes);
        const [ceiledHours, ceiledMins] = ceiledTime.split(':');
        setTempBreakStart({ hours: ceiledHours, minutes: ceiledMins });
        setTempBreakEnd({ hours: ceiledHours, minutes: ceiledMins });
        setErrors((prev) => ({ ...prev, [index]: {} }));
    };

    const editBreak = (breakData, breakIndex, rowIndex) => {
        if (!availability[rowIndex].isActive) return;
        setTimeIndex(rowIndex);
        setBrkIndex(breakIndex);
        setModalType('break');
        setModalIsOpen(true);
        const [sHours, sMinutes] = breakData.breakStart.split(':');
        const [eHours, eMinutes] = breakData.breakEnd.split(':');
        setTempBreakStart({ hours: sHours, minutes: sMinutes });
        setTempBreakEnd({ hours: eHours, minutes: eMinutes });
        setErrors((prev) => ({ ...prev, [rowIndex]: {} }));
    };

    const deleteBreak = (breakIndex, rowIndex) => {
        if (!availability[rowIndex].isActive) return;
        const updatedAvailability = availability.map((item, idx) => ({
            ...item,
            breaks: [...item.breaks],
        }));
        updatedAvailability[rowIndex].breaks.splice(breakIndex, 1);
        setAvailability(updatedAvailability);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalType(null);
        setBrkIndex(null);
        setTempBreakStart({ hours: '00', minutes: '00' });
        setTempBreakEnd({ hours: '00', minutes: '00' });
        setTempTime({ hours: '00', minutes: '00' });
        setErrors((prev) => ({ ...prev, [timeIndex]: {} }));
    };

    const validateTime = (time) => {
        return time && /^\d{2}:\d{2}$/.test(time);
    };

    const timeToMinutes = (time) => {
        if (!time) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const minutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60) % 24;
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    const timeAddMinutes = (time, add) => {
        const minutes = timeToMinutes(time) + add;
        const hours = Math.floor(minutes / 60) % 24;
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    const ceilToNext15 = (minutes) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        const multiple = Math.ceil(m / 15);
        let newM = multiple * 15;
        let addH = 0;
        if (newM >= 60) {
            newM -= 60;
            addH = 1;
        }
        return (h + addH) * 60 + newM;
    };

    useEffect(() => {
        if (modalType === 'break' && tempBreakStart.hours && tempBreakStart.minutes) {
            const startMinutes = timeToMinutes(`${tempBreakStart.hours}:${tempBreakStart.minutes}`);
            const dayStart = availability[timeIndex]?.start;
            const dayEnd = availability[timeIndex]?.end;
            
            // Calculate minimum break duration (45 minutes)
            const minBreakDuration = 45;
            const minEndMinutes = startMinutes + minBreakDuration;
            
            // Ensure break end doesn't exceed day end time
            const maxEndMinutes = dayEnd ? timeToMinutes(dayEnd) : 24 * 60;
            const finalEndMinutes = Math.min(minEndMinutes, maxEndMinutes);
            
            const [minEndHours, minEndMinutesStr] = minutesToTime(finalEndMinutes).split(':');
            
            // Only update if the calculated end time is different from current
            const currentEndTime = `${tempBreakEnd.hours}:${tempBreakEnd.minutes}`;
            const newEndTime = `${minEndHours}:${minEndMinutesStr}`;
            
            if (currentEndTime !== newEndTime) {
                setTempBreakEnd({
                    hours: minEndHours,
                    minutes: minEndMinutesStr,
                });
            }
        }
    }, [tempBreakStart.hours, tempBreakStart.minutes, modalType, timeIndex, availability]);

    // Ensure selected hour and minute values are always valid
    useEffect(() => {
        if (timeIndex === null || !availability[timeIndex]) return;
        
        if (modalType === 'break') {
            // Validate break start hours and minutes
            const startHourOptions = getFilteredHourOptions(availability[timeIndex].start, availability[timeIndex].end);
            if (startHourOptions.length > 0 && !startHourOptions.includes(tempBreakStart.hours)) {
                setTempBreakStart({ hours: startHourOptions[0], minutes: '00' });
                return; // Let the next render handle minutes
            }
            
            const startMinuteOptions = getFilteredMinuteOptions(tempBreakStart.hours, availability[timeIndex].start, availability[timeIndex].end);
            if (startMinuteOptions.length > 0 && !startMinuteOptions.includes(tempBreakStart.minutes)) {
                setTempBreakStart(prev => ({ ...prev, minutes: startMinuteOptions[0] }));
            }
            
            // Validate break end hours and minutes
            const minBreakEnd = timeAddMinutes(`${tempBreakStart.hours}:${tempBreakStart.minutes}`, 15);
            const endHourOptions = getFilteredHourOptions(minBreakEnd, availability[timeIndex].end);
            if (endHourOptions.length > 0 && !endHourOptions.includes(tempBreakEnd.hours)) {
                setTempBreakEnd({ hours: endHourOptions[0], minutes: '00' });
                return; // Let the next render handle minutes
            }
            
            const endMinuteOptions = getFilteredMinuteOptions(tempBreakEnd.hours, minBreakEnd, availability[timeIndex].end);
            if (endMinuteOptions.length > 0 && !endMinuteOptions.includes(tempBreakEnd.minutes)) {
                setTempBreakEnd(prev => ({ ...prev, minutes: endMinuteOptions[0] }));
            }
        } else if (modalType === 'start' || modalType === 'end') {
            const minTime = modalType === 'end'
                ? (availability[timeIndex].start ? timeAddMinutes(availability[timeIndex].start, 240) : '00:00')
                : '00:00';
            
            // First validate hours
            const hourOptions = getFilteredHourOptions(minTime, '23:59');
            if (hourOptions.length > 0 && !hourOptions.includes(tempTime.hours)) {
                setTempTime({ hours: hourOptions[0], minutes: '00' });
                return; // Let the next render handle minutes
            }
            
            // Then validate minutes
            const minuteOptions = getFilteredMinuteOptions(tempTime.hours, minTime, '23:59');
            if (minuteOptions.length > 0 && !minuteOptions.includes(tempTime.minutes)) {
                setTempTime(prev => ({ ...prev, minutes: minuteOptions[0] }));
            }
        }
    }, [tempBreakStart.hours, tempBreakStart.minutes, tempBreakEnd.hours, tempTime.hours, tempTime.minutes, modalType, timeIndex, availability, modalIsOpen]);

    const submitTime = () => {
        const newErrors = {};
        if (modalType === 'break') {
            const breakStartTime = `${tempBreakStart.hours}:${tempBreakStart.minutes}`;
            const breakEndTime = `${tempBreakEnd.hours}:${tempBreakEnd.minutes}`;
            const dayStart = availability[timeIndex].start;
            const dayEnd = availability[timeIndex].end;

            if (!tempBreakStart.hours || !tempBreakStart.minutes || !tempBreakEnd.hours || !tempBreakEnd.minutes) {
                newErrors.breakStart = !tempBreakStart.hours || !tempBreakStart.minutes ? t('availabilitytab.required') : '';
                newErrors.breakEnd = !tempBreakEnd.hours || !tempBreakEnd.minutes ? t('availabilitytab.required') : '';
            } else if (!validateTime(breakStartTime) || !validateTime(breakEndTime)) {
                newErrors.breakStart = !validateTime(breakStartTime) ? t('availabilitytab.invalidTime') : '';
                newErrors.breakEnd = !validateTime(breakEndTime) ? t('availabilitytab.invalidTime') : '';
            } else if (timeToMinutes(breakStartTime) >= timeToMinutes(breakEndTime)) {
                newErrors.breakEnd = t('availabilitytab.invalidBreakTime');
            } else if (dayStart && dayEnd && (timeToMinutes(breakStartTime) < timeToMinutes(dayStart) || timeToMinutes(breakEndTime) > timeToMinutes(dayEnd))) {
                newErrors.breakStart = t('availabilitytab.invalidTimeRange');
                newErrors.breakEnd = t('availabilitytab.invalidTimeRange');
            } else if (timeToMinutes(breakEndTime) - timeToMinutes(breakStartTime) < 15) {
                newErrors.breakEnd = t('availabilitytab.minBreakDuration');
            } else if (timeToMinutes(breakEndTime) - timeToMinutes(breakStartTime) > 480) {
                newErrors.breakEnd = t('availabilitytab.maxBreakDuration');
            }

            if (Object.keys(newErrors).length === 0) {
                let currentBreaks = [...availability[timeIndex].breaks];
                if (brkIndex !== null) {
                    currentBreaks.splice(brkIndex, 1);
                }
                currentBreaks.push({ breakStart: breakStartTime, breakEnd: breakEndTime });
                currentBreaks.sort((a, b) => timeToMinutes(a.breakStart) - timeToMinutes(b.breakStart));
                let hasOverlap = false;
                for (let i = 0; i < currentBreaks.length - 1; i++) {
                    if (timeToMinutes(currentBreaks[i].breakEnd) > timeToMinutes(currentBreaks[i + 1].breakStart)) {
                        hasOverlap = true;
                        break;
                    }
                }
                if (hasOverlap) {
                    newErrors.breakEnd = t('availabilitytab.breakOverlap');
                }
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors((prev) => ({ ...prev, [timeIndex]: newErrors }));
                return;
            }

            const updatedAvailability = availability.map((item, idx) => ({
                ...item,
                breaks: [...item.breaks],
            }));

            if (brkIndex !== null) {
                updatedAvailability[timeIndex].breaks[brkIndex] = { breakStart: breakStartTime, breakEnd: breakEndTime };
            } else {
                updatedAvailability[timeIndex].breaks = [
                    ...updatedAvailability[timeIndex].breaks,
                    { breakStart: breakStartTime, breakEnd: breakEndTime },
                ];
            }
            updatedAvailability[timeIndex].breaks.sort((a, b) => timeToMinutes(a.breakStart) - timeToMinutes(b.breakStart));

            setAvailability(updatedAvailability);
            setModalIsOpen(false);
            setErrors((prev) => ({ ...prev, [timeIndex]: {} }));
        } else {
            if (!tempTime.hours || !tempTime.minutes) {
                newErrors[modalType] = t('availabilitytab.required');
            } else if (modalType === 'end' && availability[timeIndex].start) {
                const startTime = availability[timeIndex].start;
                const endTime = `${tempTime.hours}:${tempTime.minutes}`;
                const startMinutes = timeToMinutes(startTime);
                const endMinutes = timeToMinutes(endTime);
                
                if (startMinutes >= endMinutes) {
                    newErrors.end = t('availabilitytab.invalidEndTime');
                } else if (endMinutes - startMinutes < 240) {
                    newErrors.end = t('availabilitytab.minDuration');
                } else if (endMinutes - startMinutes > 1440) {
                    newErrors.end = t('availabilitytab.maxDuration');
                }
            } else if (modalType === 'start' && availability[timeIndex].end) {
                const startTime = `${tempTime.hours}:${tempTime.minutes}`;
                const endTime = availability[timeIndex].end;
                const startMinutes = timeToMinutes(startTime);
                const endMinutes = timeToMinutes(endTime);
                
                if (startMinutes >= endMinutes) {
                    newErrors.start = t('availabilitytab.invalidStartTime');
                } else if (endMinutes - startMinutes < 240) {
                    newErrors.start = t('availabilitytab.minDuration');
                } else if (endMinutes - startMinutes > 1440) {
                    newErrors.start = t('availabilitytab.maxDuration');
                }
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors((prev) => ({ ...prev, [timeIndex]: newErrors }));
                return;
            }

            const updatedAvailability = availability.map((item, idx) => ({
                ...item,
                breaks: [...item.breaks],
            }));

            const newTime = `${tempTime.hours}:${tempTime.minutes}`;
            updatedAvailability[timeIndex] = {
                ...updatedAvailability[timeIndex],
                [modalType]: newTime,
            };
            setAvailability(updatedAvailability);
            setModalIsOpen(false);
            setErrors((prev) => ({ ...prev, [timeIndex]: {} }));
        }
    };

    const getFilteredHourOptions = (minTime, maxTime) => {
        const minMinutes = timeToMinutes(minTime);
        const maxMinutes = timeToMinutes(maxTime);
        const options = [];
        
        // Get the starting hour (minimum hour available)
        const minHour = Math.floor(minMinutes / 60);
        // Get the maximum hour (but don't exceed 23)
        const maxHour = Math.min(23, Math.floor(maxMinutes / 60));
        
        for (let h = minHour; h <= maxHour; h++) {
            // For the maximum hour, check if it has any valid minutes
            if (h === maxHour) {
                const hourMinutes = h * 60;
                // Allow the exact max time for break end times
                if (hourMinutes <= maxMinutes) {
                    options.push(h.toString().padStart(2, '0'));
                }
            } else {
                options.push(h.toString().padStart(2, '0'));
            }
        }
        return options;
    };

    const getFilteredMinuteOptions = (hour, minTime, maxTime) => {
        const minMinutes = timeToMinutes(minTime);
        const maxMinutes = timeToMinutes(maxTime);
        const hourMinutes = parseInt(hour) * 60;
        const options = ['00', '15', '30', '45'];
        
        return options.filter((m) => {
            const totalMinutes = hourMinutes + parseInt(m);
            // Allow the exact max time (e.g., 14:00) for break end times
            return totalMinutes >= minMinutes && totalMinutes <= maxMinutes;
        });
    };

    const toggleActive = (index) => {
        const updated = availability.map((item, idx) => ({
            ...item,
            breaks: [...item.breaks],
        }));
        updated[index] = { ...updated[index], isActive: !updated[index].isActive };
        setAvailability(updated);
    };

    const handleLocationChange = (index, value) => {
        if (!availability[index].isActive) return;
        const updated = availability.map((item, idx) => ({
            ...item,
            breaks: [...item.breaks],
        }));
        updated[index] = { ...updated[index], addressType: value };
        setAvailability(updated);
        // Clear the addressType error when a location is selected
        if (value) {
            setErrors(prev => ({
                ...prev,
                [index]: { ...prev[index], addressType: '' }
            }));
        }
    };

    const handleReplicateApply = () => {
        if (!replicateSourceDay || replicateTargetDays.length === 0) return;
        const sourceData = availability.find((item) => item.day === replicateSourceDay);
        if (!sourceData) return;
        setAvailability(prev =>
            prev.map((item) => {
                if (!replicateTargetDays.includes(item.day)) return item;
                return {
                    ...item,
                    start: sourceData.start,
                    end: sourceData.end,
                    breaks: [...sourceData.breaks],
                    addressType: sourceData.addressType,
                    isActive: sourceData.isActive,
                };
            })
        );
        setErrors({});
        setShowReplicateModal(false);
        setReplicateSourceDay('');
        setReplicateTargetDays([]);
    };

    const closeReplicateModal = () => {
        setShowReplicateModal(false);
        setReplicateSourceDay('');
        setReplicateTargetDays([]);
    };

    const toggleReplicateTarget = (day) => {
        setReplicateTargetDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        );
    };

    const handleReset = async () => {
        const result = await Swal.fire({
            title: t('availabilitytab.resetConfirmTitle'),
            text: t('availabilitytab.resetConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('availabilitytab.resetConfirmButton'),
            cancelButtonText: t('availabilitytab.resetCancelButton'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (result.isConfirmed) {
            const days = [
                'monday',
                'tuesday',
                'wednesday',
                'thursday',
                'friday',
                'saturday',
                'sunday'
            ];

            const resetAvailability = days.map((day) => ({
                day,
                start: '',
                end: '',
                isActive: false,
                breaks: [],
                addressType: '',
            }));

            try {
                setIsSubmitting(true);

                await dispatch(
                    updateAvailability({
                        availability: { availability: [] }
                    })
                ).unwrap();

                setAvailability(resetAvailability);
                setInitialAvailability(JSON.stringify(resetAvailability));
                setHasChanges(false);
                setErrors({});

                toast.success(t('availabilitytab.resetSuccessText'));

            } catch (error) {
                toast.warn(t('availabilitytab.resetErrorText'));
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const validateAvailability = () => {
        const newErrors = {};
        availability.forEach((item, index) => {
            if (item.isActive) {
                const rowErrors = {};
                if (!item.start) rowErrors.start = t('availabilitytab.required');
                else if (!validateTime(item.start)) rowErrors.start = t('availabilitytab.invalidTime');
                if (!item.end) rowErrors.end = t('availabilitytab.required');
                else if (!validateTime(item.end)) rowErrors.end = t('availabilitytab.invalidTime');
                if (item.start && item.end) {
                    if (timeToMinutes(item.start) >= timeToMinutes(item.end)) rowErrors.end = t('availabilitytab.invalidEndTime');
                    if (timeToMinutes(item.end) - timeToMinutes(item.start) < 240) rowErrors.end = t('availabilitytab.minDuration');
                }
                if (!item.addressType) rowErrors.addressType = t('availabilitytab.required');
                item.breaks.forEach((brk, brkIdx) => {
                    if (!validateTime(brk.breakStart) || !validateTime(brk.breakEnd)) {
                        rowErrors[`break_${brkIdx}`] = t('availabilitytab.invalidTime');
                    } else if (timeToMinutes(brk.breakStart) >= timeToMinutes(brk.breakEnd)) {
                        rowErrors[`break_${brkIdx}`] = t('availabilitytab.invalidBreakTime');
                    } else if (timeToMinutes(brk.breakStart) < timeToMinutes(item.start) || timeToMinutes(brk.breakEnd) > timeToMinutes(item.end)) {
                        rowErrors[`break_${brkIdx}`] = t('availabilitytab.invalidTimeRange');
                    }
                });
                const sortedBreaks = [...item.breaks].sort((a, b) => timeToMinutes(a.breakStart) - timeToMinutes(b.breakStart));
                let hasOverlap = false;
                for (let i = 0; i < sortedBreaks.length - 1; i++) {
                    if (timeToMinutes(sortedBreaks[i].breakEnd) > timeToMinutes(sortedBreaks[i + 1].breakStart)) {
                        hasOverlap = true;
                        break;
                    }
                }
                if (hasOverlap) rowErrors.breaks = t('availabilitytab.breakOverlap');
                if (Object.keys(rowErrors).length > 0) newErrors[index] = rowErrors;
            }
        });
        return newErrors;
    };

    const handleUpdate = async () => {
        const newErrors = validateAvailability();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const result = await Swal.fire({
            title: t('availabilitytab.updateConfirmTitle'),
            text: t('availabilitytab.updateConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('availabilitytab.updateConfirmButton'),
            cancelButtonText: t('availabilitytab.updateCancelButton'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
        });

        if (result.isConfirmed) {
            let updatedAvailability = availability.map((item, index) => {
                const existing = selectedDoctor?.availability?.find(
                    (avail) => avail.day.toLowerCase() === item.day
                );

                return {
                    availabilityId: existing?.availabilityId,
                    day: item.day.charAt(0).toUpperCase() + item.day.slice(1),
                    start: item.start,
                    end: item.end,
                    isActive: item.isActive,
                    breaks: [...item.breaks],
                    addressType: item.addressType,
                    isHybridEdit: existing?.isHybridEdit || false,
                    crossesMidnight: existing?.crossesMidnight || false,
                    startDayShift: existing?.startDayShift || 0,
                    endDayShift: existing?.endDayShift || 0,
                    startUTC: existing?.startUTC,
                    endUTC: existing?.endUTC,
                    // leaveDates: existing?.leaveDates || [],
                };
            });

            try {
                setIsSubmitting(true);

                await dispatch(
                    updateAvailability({
                        doctorId,availability: { availability: updatedAvailability },
                    })
                ).unwrap();

                setInitialAvailability(JSON.stringify(availability));
                setHasChanges(false);

                toast.success(t('availabilitytab.updateSuccessText'));
            } catch (error) {
                console.log('Availability update error:', JSON.stringify(error, null, 2));
                const errData = error?.error?.response?.data || error?.response?.data || error?.data || error;
                const errorMessage =
                    errData?.message?.[language] || errData?.[language] ||
                    t('availabilitytab.updateErrorText');

                // Check if error has Days array (new error format)
                const conflictingDays = errData?.Days || [];
                
                const isDeactivateError =
                    (language === 'en' &&
                        errorMessage.includes('Cannot deactivate slot.')) ||
                    (language === 'fr' &&
                        errorMessage.includes('Impossible de désactiver la'));

                if (isDeactivateError && conflictingDays.length > 0) {
                    // New error format with Days array
                    const daysText = conflictingDays.join(', ');

                    // Special alert for deactivation error with multiple days
                    const conflictResult = await Swal.fire({
                        title: t('availabilitytab.warning_title', 'Warning'),
                        html: `
            <p>${t('availabilitytab.deactivationErrorMultipleDaysText', { days: daysText }) ||
                            `Cannot deactivate slots. Appointments are already booked on: ${daysText}`}</p>
            <p class="mt-2">${t('availabilitytab.deactivationOptionsText') ||
                            'Please choose an option to proceed:'}</p>
          `,
                        icon: 'warning',
                        showCancelButton: true,
                        showDenyButton: true,
                        confirmButtonText: t('availabilitytab.reschedule'),
                        denyButtonText: t('availabilitytab.makeHybrid'),
                        cancelButtonText: t('availabilitytab.cancelAllAppointments'),
                        confirmButtonColor: '#4ABDBD',
                        denyButtonColor: '#FFA500',
                        cancelButtonColor: '#d33',
                    });

                    if (conflictResult.isConfirmed) {
                        // Reschedule option: Inform user to manually reschedule appointments
                        toast.warn(t('availabilitytab.rescheduleConfirmMultipleDaysText', { days: daysText }) ||
                                `Please manually reschedule all appointments on ${daysText} before deactivating.`);
                        // Optionally, redirect to an appointment management page or trigger an action
                    } else if (conflictResult.isDenied) {
                        // Make Hybrid option - set isHybridEdit for all conflicting days
                        const updatedAvailabilityWithHybrid = updatedAvailability.map((item) => {
                            const dayName = item.day.toLowerCase();
                            const isConflictingDay = conflictingDays.some(
                                (day) => day.toLowerCase() === dayName
                            );
                            if (isConflictingDay) {
                                return { ...item, isHybridEdit: true };
                            }
                            return item;
                        });

                        try {
                            setIsSubmitting(true);
                            await dispatch(
                                updateAvailability({
                                    doctorId,
                                    availability: { availability: updatedAvailabilityWithHybrid },
                                })
                            ).unwrap();
                            setInitialAvailability(JSON.stringify(availability));
                            setHasChanges(false);
                            toast.success(t('availabilitytab.hybridSuccessMultipleDaysText', { days: daysText }) ||
                                    `${daysText} have been set to hybrid mode. Please manage any booked appointments.`);
                        } catch (newError) {
                            const newErrorMessage =
                                newError?.error?.response?.data?.message?.[language] || newError?.error?.response?.data?.[language] ||
                                t('availabilitytab.updateErrorText');
                            toast.warn(newErrorMessage);
                        } finally {
                            setIsSubmitting(false);
                        }
                    } else if (conflictResult.dismiss === Swal.DismissReason.cancel) {
                        // Cancel All Appointments option
                        const cancelConfirm = await Swal.fire({
                            title: t('availabilitytab.cancelAllConfirmTitle'),
                            text: t('availabilitytab.cancelAllConfirmMultipleDaysText', { days: daysText }) ||
                                `Are you sure you want to cancel all appointments on ${daysText}?`,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: t('availabilitytab.yesCancelAll'),
                            cancelButtonText: t('availabilitytab.no'),
                            confirmButtonColor: '#4ABDBD',
                            cancelButtonColor: '#4ABDBD',
                        });

                        if (cancelConfirm.isConfirmed) {
                            try {
                                // Dispatch cancelAllAppointments with all conflicting days as array
                                const daysArray = conflictingDays.map(day => day.charAt(0).toUpperCase() + day.slice(1));
                                await dispatch(
                                    cancelAllAppointments({
                                        doctorId: selectedDoctor.doctorId,
                                        days: daysArray,
                                    })
                                ).unwrap();

                                // If cancel succeeds, proceed to update availability
                                try {
                                    await dispatch(
                                        updateAvailability({
                                            doctorId,
                                            availability: { availability: updatedAvailability },
                                        })
                                    ).unwrap();

                                    setInitialAvailability(JSON.stringify(availability));
                                    setHasChanges(false);

                                    toast.success(t('availabilitytab.cancelAndUpdateSuccessMultipleDaysText', { days: daysText }) ||
                                            `All appointments on ${daysText} have been cancelled and availability updated.`);
                                } catch (updateError) {
                                    const updateErrorMessage =
                                        updateError?.error?.response?.data?.message?.[language] || updateError?.error?.response?.data?.[language] ||
                                        t('availabilitytab.updateErrorText');
                                    toast.warn(updateErrorMessage);
                                }
                            } catch (cancelError) {
                                const cancelErrorMessage =
                                    cancelError?.error?.response?.data?.message?.[language] || cancelError?.error?.response?.data?.[language] ||
                                    t('availabilitytab.cancelErrorText');
                                toast.warn(cancelErrorMessage);
                                // Do not reset availability changes
                            }
                        }
                        // If not confirmed, do nothing (keep changes)
                    }
                } else if (isDeactivateError) {
                    // Old error format (single day - for backward compatibility)
                    const dayMatch = errorMessage.match(/ \(([^)]+)\)\./);
                    const conflictingDay = dayMatch
                        ? dayMatch[1].toLowerCase()
                        : t('availabilitytab.unknownDay');

                    // Special alert for deactivation error
                    const conflictResult = await Swal.fire({
                        title: t('availabilitytab.warning_title', 'Warning'),
                        html: `
            <p>${t('availabilitytab.deactivationErrorText', { day: conflictingDay }) ||
                            `Cannot deactivate ${conflictingDay} because appointments are already booked.`}</p>
            <p class="mt-2">${t('availabilitytab.deactivationOptionsText') ||
                            'Please choose an option to proceed:'}</p>
          `,
                        icon: 'warning',
                        showCancelButton: true,
                        showDenyButton: true,
                        confirmButtonText: t('availabilitytab.reschedule'),
                        denyButtonText: t('availabilitytab.makeHybrid'),
                        cancelButtonText: t('availabilitytab.cancelAllAppointments'),
                        confirmButtonColor: '#4ABDBD',
                        denyButtonColor: '#FFA500',
                        cancelButtonColor: '#d33',
                    });

                    if (conflictResult.isConfirmed) {
                        // Reschedule option: Inform user to manually reschedule appointments
                        toast.warn(t('availabilitytab.rescheduleConfirmText', { day: conflictingDay }) ||
                                `Please manually reschedule all appointments on ${conflictingDay} before deactivating the day.`);
                        // Optionally, redirect to an appointment management page or trigger an action
                    } else if (conflictResult.isDenied) {
                        // Make Hybrid option
                        const conflictingIndex = updatedAvailability.findIndex(
                            (item) => item.day.toLowerCase() === conflictingDay
                        );
                        if (conflictingIndex !== -1) {
                            updatedAvailability[conflictingIndex].isHybridEdit = true;
                            try {
                                setIsSubmitting(true);
                                await dispatch(
                                    updateAvailability({
                                        doctorId,availability: { availability: updatedAvailability },
                                    })
                                ).unwrap();
                                setInitialAvailability(JSON.stringify(availability));
                                setHasChanges(false);
                                toast.success(t('availabilitytab.hybridSuccessText', {day: conflictingDay}) ||
                                        `The day ${conflictingDay} has been set to hybrid mode. Please manage any booked appointments.`);
                            } catch (newError) {
                                const newErrorMessage =
                                    newError?.error?.response?.data?.message?.[language] || newError?.error?.response?.data?.[language] ||
                                    t('availabilitytab.updateErrorText');
                                toast.warn(newErrorMessage);
                            } finally {
                                setIsSubmitting(false);
                            }
                        }
                    } else if (conflictResult.dismiss === Swal.DismissReason.cancel) {
                        // Cancel All Appointments option
                        const cancelConfirm = await Swal.fire({
                            title: t('availabilitytab.cancelAllConfirmTitle'),
                            text: t('availabilitytab.cancelAllConfirmText', { day: conflictingDay }) ||
                                `Are you sure you want to cancel all appointments on ${conflictingDay}?`,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: t('availabilitytab.yesCancelAll'),
                            cancelButtonText: t('availabilitytab.no'),
                            confirmButtonColor: '#4ABDBD',
                            cancelButtonColor: '#4ABDBD',
                        });

                        if (cancelConfirm.isConfirmed) {
                            try {
                                // Dispatch cancelAllAppointments
                                await dispatch(
                                    cancelAllAppointments({
                                        doctorId: selectedDoctor.doctorId, // Adjust based on actual field (e.g., selectedDoctor._id or selectedDoctor.id if different)
                                        day: conflictingDay.charAt(0).toUpperCase() + conflictingDay.slice(1),
                                    })
                                ).unwrap();

                                // If cancel succeeds, proceed to update availability
                                try {
                                    await dispatch(
                                        updateAvailability({
                                            doctorId,availability: { availability: updatedAvailability },
                                        })
                                    ).unwrap();

                                    setInitialAvailability(JSON.stringify(availability));
                                    setHasChanges(false);

                                    toast.success(t('availabilitytab.cancelAndUpdateSuccessText', { day: conflictingDay}) ||
                                            `All appointments on ${conflictingDay} have been cancelled and availability updated.`);
                                } catch (updateError) {
                                    const updateErrorMessage =
                                        updateError?.error?.response?.data?.message?.[language] || updateError?.error?.response?.data?.[language] ||
                                        t('availabilitytab.updateErrorText');
                                    toast.warn(updateErrorMessage);
                                }
                            } catch (cancelError) {
                                const cancelErrorMessage =
                                    cancelError?.error?.response?.data?.message?.[language] || cancelError?.error?.response?.data?.[language] ||
                                    t('availabilitytab.cancelErrorText');
                                toast.warn(cancelErrorMessage);
                                // Do not reset availability changes
                            }
                        }
                        // If not confirmed, do nothing (keep changes)
                    }
                } else {
                    toast.warn(errorMessage);
                }
            } finally {
                setIsSubmitting(false);
            }
        }
    };


    return (
        <div data-testid="availability-tab-container" className="mt-4">
            {/* Header Row */}
            <div data-testid="availability-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="availability-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('availabilitytab.heading')}
                </h2>
                <button
                    data-testid="replicate-button"
                    onClick={() => setShowReplicateModal(true)}
                    disabled={isSubmitting}
                    className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <Copy className="w-4 h-4" />
                    <span data-testid="replicate-button-text">{t('availabilitytab.button_replicate')}</span>
                </button>
            </div>
            {/* Table for larger screens, Cards for mobile */}
            <div className="hidden sm:block overflow-x-auto rounded-2xl shadow">
                <table data-testid="availability-table" className="w-full border-collapse rounded-2xl overflow-hidden">
                    <thead>
                        <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                            <th data-testid="day-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('availabilitytab.table_header_day')}</th>
                            <th data-testid="start-time-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('availabilitytab.table_header_start_time')}</th>
                            <th data-testid="end-time-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('availabilitytab.table_header_end_time')}</th>
                            <th data-testid="breaks-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('availabilitytab.table_header_breaks')}</th>
                            <th data-testid="active-days-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('availabilitytab.table_header_active_days')}</th>
                            <th data-testid="location-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('availabilitytab.table_header_location')}</th>
                        </tr>
                    </thead>
                    <tbody data-testid="availability-table-body">
                        {availability.map((item, index) => (
                            <tr key={index} data-testid={`availability-row-${index}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                <td data-testid={`day-cell-${index}`} className="p-3">
                                    <div data-testid={`day-content-${index}`} className="flex gap-2 items-center">
                                        <span data-testid={`day-name-${index}`} className={`text-sm font-semibold group-hover:text-gray-900 transition-colors duration-300 ${item.isActive ? 'text-[#4ABDBD]' : 'text-gray-400'}`}>
                                            {t(`availabilitytab.day${index + 1}`).toUpperCase()}
                                        </span>
                                    </div>
                                </td>
                                <td data-testid={`start-time-cell-${index}`} className="p-3">
                                    {item.start ? (
                                        <div data-testid={`start-time-display-${index}`} className="flex items-center gap-2">
                                            <span
                                                data-testid={`start-time-value-${index}`}
                                                onClick={() => openTimeModal(index, 'start')}
                                                className={`text-sm font-semibold transition-colors duration-300 ${item.isActive ? 'text-gray-700 group-hover:text-gray-900 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                                            >
                                                {item.start}
                                            </span>
                                            <button
                                                data-testid={`edit-start-time-${index}`}
                                                onClick={() => openTimeModal(index, 'start')}
                                                className={`group/btn text-[#4ABDBD] hover:text-white p-1 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden ${!item.isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            data-testid={`add-start-time-${index}`}
                                            onClick={() => openTimeModal(index, 'start')}
                                            className={`group/btn text-white p-2 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden ${item.isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] cursor-pointer hover:scale-110' : 'bg-gradient-to-r from-gray-300 to-gray-400 cursor-not-allowed opacity-50'}`}
                                        >
                                            <Plus className="h-2.5 w-2.5" />
                                        </button>
                                    )}
                                    {errors[index]?.start && (
                                        <p data-testid={`start-time-error-${index}`} className="text-red-500 text-xs mt-1">{errors[index].start}</p>
                                    )}
                                </td>
                                <td data-testid={`end-time-cell-${index}`} className="p-3">
                                    {item.end ? (
                                        <div data-testid={`end-time-display-${index}`} className="flex items-center gap-2">
                                            <span
                                                data-testid={`end-time-value-${index}`}
                                                onClick={() => openTimeModal(index, 'end')}
                                                className={`text-sm font-semibold transition-colors duration-300 ${item.isActive ? 'text-gray-700 group-hover:text-gray-900 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                                            >
                                                {item.end}
                                            </span>
                                            <button
                                                data-testid={`edit-end-time-${index}`}
                                                onClick={() => openTimeModal(index, 'end')}
                                                className={`group/btn text-[#4ABDBD] hover:text-white p-1 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden ${!item.isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            data-testid={`add-end-time-${index}`}
                                            onClick={() => openTimeModal(index, 'end')}
                                            className={`group/btn text-white p-2 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden ${item.isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] cursor-pointer hover:scale-110' : 'bg-gradient-to-r from-gray-300 to-gray-400 cursor-not-allowed opacity-50'}`}
                                        >
                                            <Plus className="h-2.5 w-2.5" />
                                        </button>
                                    )}
                                    {errors[index]?.end && (
                                        <p data-testid={`end-time-error-${index}`} className="text-red-500 text-xs mt-1">{errors[index].end}</p>
                                    )}
                                </td>
                                <td data-testid={`breaks-cell-${index}`} className="p-3">
                                    <div data-testid={`breaks-content-${index}`} className="flex gap-2 items-center">
                                        <button
                                            data-testid={`add-break-${index}`}
                                            onClick={() => addAvailabilityBreak(index)}
                                            className={`group/btn text-white p-2 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden ${item.isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:from-[#3AA8A8] hover:to-[#2D8A8A] cursor-pointer hover:scale-110' : 'bg-gradient-to-r from-gray-300 to-gray-400 cursor-not-allowed opacity-50'}`}
                                        >
                                            <Plus className="h-2.5 w-2.5" />
                                        </button>
                                        <ul className="list-disc space-y-1">
                                            {item.breaks.map((breakData, breakIndex) => (
                                                <li key={breakIndex} data-testid={`break-item-${index}-${breakIndex}`} className={`text-sm font-semibold transition-colors duration-300 flex gap-2 items-center ${item.isActive ? 'text-gray-700 group-hover:text-gray-900' : 'text-gray-400'}`}>
                                                    <span data-testid={`break-item-${index}-${breakIndex}-time`}>{breakData.breakStart} - {breakData.breakEnd}</span>
                                                    <div className="flex gap-1">
                                                        <button
                                                            data-testid={`edit-break-${breakIndex}`}
                                                            onClick={() => editBreak(breakData, breakIndex, index)}
                                                            className={`group/btn text-[#4ABDBD] hover:text-white p-1 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden ${!item.isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            data-testid={`delete-break-${breakIndex}`}
                                                            onClick={() => deleteBreak(breakIndex, index)}
                                                            className={`group/btn text-red-500 hover:text-white p-1 rounded-lg bg-gradient-to-r from-red-50 to-pink-50 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden ${!item.isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {errors[index]?.breakStart && (
                                        <p className="text-red-500 text-xs mt-1">{errors[index].breakStart}</p>
                                    )}
                                    {errors[index]?.breakEnd && (
                                        <p className="text-red-500 text-xs mt-1">{errors[index].breakEnd}</p>
                                    )}
                                    {item.breaks.map((_, brkIdx) => (
                                        errors[index]?.[`break_${brkIdx}`] && (
                                            <p key={brkIdx} className="text-red-500 text-xs mt-1">{errors[index][`break_${brkIdx}`]}</p>
                                        )
                                    ))}
                                    {errors[index]?.breaks && (
                                        <p className="text-red-500 text-xs mt-1">{errors[index].breaks}</p>
                                    )}
                                </td>
                                <td data-testid={`active-days-cell-${index}`} className="p-3">
                                    <div className="relative">
                                        <div
                                            data-testid={`toggle-${index}`}
                                            className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 cursor-pointer ${
                                                item.isActive ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                                            }`}
                                            onClick={() => toggleActive(index)}
                                        >
                                            <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                                item.isActive
                                                    ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                    : 'translate-x-1'
                                            }`}></span>
                                        </div>
                                    </div>
                                </td>
                                <td data-testid={`location-cell-${index}`} className="p-3">
                                    <CustomSelect
                                        id={`location-select-${index}`}
                                        value={item.addressType}
                                        onChange={(value) => handleLocationChange(index, value)}
                                        options={[
                                            { value: '', label: t('availabilitytab.select_location_placeholder') },
                                            ...alladdress.map((addr) => ({
                                                value: addr.addressId,
                                                label: addr.addressType
                                            }))
                                        ]}
                                        placeholder={t('availabilitytab.select_location_placeholder')}
                                        disabled={!item.isActive}
                                        error={!!errors[index]?.addressType}
                                        className="p-2 text-xs"
                                        dropdownClassName="z-[9999]"
                                        isOpen={openDropdown === `location-desktop-${index}`}
                                        onToggle={(isOpen) => setOpenDropdown(isOpen ? `location-desktop-${index}` : null)}
                                        testId={`location-select-${index}`}
                                        usePortal={true}
                                    />
                                    {errors[index]?.addressType && (
                                        <p data-testid={`location-error-${index}`} className="text-red-500 text-xs mt-1">{errors[index].addressType}</p>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Card layout for mobile screens */}
            <div className="block sm:hidden space-y-4">
                {availability.map((item, index) => (
                    <div
                        key={index}
                        data-testid={`availability-card-mobile-${index}`}
                        className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                                <span data-testid={`day-name-mobile-${index}`} className={`text-sm font-semibold ${item.isActive ? 'text-[#4ABDBD]' : 'text-gray-400'}`}>
                                    {t(`availabilitytab.day${index + 1}`).toUpperCase()}
                                </span>
                            </div>
                            <div className="relative">
                                <div
                                    data-testid={`toggle-mobile-${index}`}
                                    className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 cursor-pointer ${
                                        item.isActive ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                                    }`}
                                    onClick={() => toggleActive(index)}
                                >
                                    <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                        item.isActive
                                            ? 'translate-x-[24px] sm:translate-x-[24px]'
                                            : 'translate-x-1'
                                    }`}></span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-600">{t('availabilitytab.table_header_start_time')}:</span>
                                {item.start ? (
                                    <div className="flex items-center gap-2">
                                        <span data-testid={`start-time-value-mobile-${index}`} className={`text-sm font-semibold ${item.isActive ? 'text-gray-700' : 'text-gray-400'}`}>{item.start}</span>
                                        <button
                                            data-testid={`edit-start-time-mobile-${index}`}
                                            onClick={() => openTimeModal(index, 'start')}
                                            className="text-[#4ABDBD] hover:text-white p-1 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                        >
                                            <Edit className="h-3 w-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        data-testid={`add-start-time-mobile-${index}`}
                                        onClick={() => openTimeModal(index, 'start')}
                                        className="text-white p-2 rounded-xl bg-gradient-to-r from-gray-400 to-gray-500 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                    >
                                        <Plus className="h-2.5 w-2.5" />
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-600">{t('availabilitytab.table_header_end_time')}:</span>
                                {item.end ? (
                                    <div className="flex items-center gap-2">
                                        <span data-testid={`end-time-value-mobile-${index}`} className={`text-sm font-semibold ${item.isActive ? 'text-gray-700' : 'text-gray-400'}`}>{item.end}</span>
                                        <button
                                            data-testid={`edit-end-time-mobile-${index}`}
                                            onClick={() => openTimeModal(index, 'end')}
                                            className="text-[#4ABDBD] hover:text-white p-1 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                        >
                                            <Edit className="h-3 w-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        data-testid={`add-end-time-mobile-${index}`}
                                        onClick={() => openTimeModal(index, 'end')}
                                        className="text-white p-2 rounded-xl bg-gradient-to-r from-gray-400 to-gray-500 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                    >
                                        <Plus className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                            <div>
                                <span className="text-xs font-bold text-gray-600">{t('availabilitytab.table_header_breaks')}:</span>
                                <div className="flex items-center gap-2 mt-1">
                                    <button
                                        data-testid={`add-break-mobile-${index}`}
                                        onClick={() => addAvailabilityBreak(index)}
                                        className="text-white p-2 rounded-xl bg-gradient-to-r from-gray-400 to-gray-500 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                    >
                                        <Plus className="h-3 w-3" />
                                    </button>
                                    <div className="flex flex-wrap gap-1">
                                        {item.breaks.map((breakData, breakIndex) => (
                                            <div key={breakIndex} data-testid={`break-item-mobile-${index}-${breakIndex}`} className={`flex items-center gap-1 text-sm font-semibold bg-gray-100 px-2 py-1 rounded ${item.isActive ? 'text-gray-700' : 'text-gray-400'}`}>
                                                <span>{breakData.breakStart} - {breakData.breakEnd}</span>
                                                <button
                                                    data-testid={`edit-break-mobile-${index}-${breakIndex}`}
                                                    onClick={() => editBreak(breakData, breakIndex, index)}
                                                    className="text-[#4ABDBD] hover:text-white p-1 rounded bg-blue-50 hover:bg-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                                >
                                                    <Edit className="h-3 w-3" />
                                                </button>
                                                <button
                                                    data-testid={`delete-break-mobile-${index}-${breakIndex}`}
                                                    onClick={() => deleteBreak(breakIndex, index)}
                                                    className="text-red-500 hover:text-white p-1 rounded bg-red-50 hover:bg-red-500 transition-all duration-300 cursor-pointer"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <span className="text-xs font-bold text-gray-600">{t('availabilitytab.table_header_location')}:</span>
                                <div className="mt-1">
                                    <CustomSelect
                                        id={`location-mobile-select-${index}`}
                                        value={item.addressType}
                                        onChange={(value) => handleLocationChange(index, value)}
                                        options={[
                                            { value: '', label: t('availabilitytab.select_location_placeholder') },
                                            ...alladdress.map((addr) => ({
                                                value: addr.addressId,
                                                label: addr.addressType
                                            }))
                                        ]}
                                        placeholder={t('availabilitytab.select_location_placeholder')}
                                        disabled={!item.isActive}
                                        className="p-2 text-xs"
                                        dropdownClassName="z-[9999]"
                                        isOpen={openDropdown === `location-mobile-${index}`}
                                        onToggle={(isOpen) => setOpenDropdown(isOpen ? `location-mobile-${index}` : null)}
                                        testId={`location-mobile-select-${index}`}
                                        usePortal={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <button
                    data-testid="replicate-button-mobile"
                    onClick={() => setShowReplicateModal(true)}
                    disabled={isSubmitting}
                    className={`group flex items-center justify-center gap-2 w-full cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <Copy className="w-4 h-4" />
                    <span data-testid="replicate-button-text-mobile">{t('availabilitytab.button_replicate')}</span>
                </button>
            </div>

            {/* Update Button */}
            <div className="flex justify-center items-center gap-4 pt-6">
                <button
                    data-testid="update-button"
                    onClick={handleUpdate}
                    disabled={loading || isSubmitting || !hasChanges}
                    className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-6 py-3 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${(loading || isSubmitting || !hasChanges) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {isSubmitting && (
                        <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                    )}
                    <span data-testid="update-text">{t('availabilitytab.button_update')}</span>
                </button>
            </div>

            {/* Replicate Settings Modal */}
            {showReplicateModal && (
                <div data-testid="replicate-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="p-5 border-b border-gray-200 flex justify-between items-center rounded-t-2xl bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5">
                            <div className="flex items-center gap-2">
                                <Copy className="w-5 h-5 text-[#4ABDBD]" />
                                <h2 data-testid="replicate-modal-title" className="text-base font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                                    {t('availabilitytab.replicateModalTitle')}
                                </h2>
                            </div>
                            <button
                                data-testid="replicate-modal-close"
                                onClick={closeReplicateModal}
                                className="text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
                            {/* Source Day */}
                            <div>
                                <p className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('availabilitytab.replicateSourceLabel')}
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {availability.map((item, idx) => {
                                        const isDisabled = !item.isActive;
                                        return (
                                            <label
                                                key={item.day}
                                                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 ${
                                                    isDisabled
                                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                                        : replicateSourceDay === item.day
                                                            ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 cursor-pointer'
                                                            : 'border-gray-200 hover:border-[#4ABDBD]/50 hover:bg-gray-50 cursor-pointer'
                                                }`}
                                            >
                                                <input
                                                    data-testid={`replicate-source-radio-${item.day}`}
                                                    type="radio"
                                                    name="replicateSource"
                                                    value={item.day}
                                                    checked={replicateSourceDay === item.day}
                                                    disabled={isDisabled}
                                                    onChange={() => {
                                                        if (isDisabled) return;
                                                        setReplicateSourceDay(item.day);
                                                        setReplicateTargetDays(prev => prev.filter(d => d !== item.day));
                                                    }}
                                                    className="accent-[#4ABDBD] w-4 h-4 flex-shrink-0"
                                                />
                                                <p className={`text-xs font-semibold truncate ${isDisabled ? 'text-gray-400' : 'text-[#4ABDBD]'}`}>
                                                    {t(`availabilitytab.day${idx + 1}`)}
                                                </p>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Target Days */}
                            <div>
                                <p className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('availabilitytab.replicateTargetLabel')}
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {availability.map((item, idx) => {
                                        const isSource = replicateSourceDay === item.day;
                                        const isChecked = replicateTargetDays.includes(item.day);
                                        return (
                                            <label
                                                key={item.day}
                                                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 ${
                                                    isSource
                                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                                        : isChecked
                                                            ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 cursor-pointer'
                                                            : 'border-gray-200 hover:border-[#4ABDBD]/50 hover:bg-gray-50 cursor-pointer'
                                                }`}
                                            >
                                                <input
                                                    data-testid={`replicate-target-checkbox-${item.day}`}
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isSource}
                                                    onChange={() => !isSource && toggleReplicateTarget(item.day)}
                                                    className="accent-[#4ABDBD] w-4 h-4 flex-shrink-0"
                                                />
                                                <p className={`text-xs font-semibold truncate ${isSource ? 'text-gray-400' : item.isActive ? 'text-[#4ABDBD]' : 'text-gray-400'}`}>
                                                    {t(`availabilitytab.day${idx + 1}`)}
                                                    {isSource && <span className="ml-1 text-[10px] text-gray-400">({t('availabilitytab.source_label', 'source')})</span>}
                                                </p>
                                            </label>
                                        );
                                    })}
                                </div>
                                {(!replicateSourceDay || replicateTargetDays.length === 0) && (
                                    <p className="text-xs text-gray-400 mt-3">
                                        {t('availabilitytab.replicateHint')}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-200 flex gap-3 rounded-b-2xl bg-gray-50">
                            <button
                                data-testid="replicate-close-button"
                                onClick={closeReplicateModal}
                                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-semibold text-sm cursor-pointer"
                            >
                                {t('availabilitytab.button_close')}
                            </button>
                            <button
                                data-testid="replicate-apply-button"
                                onClick={handleReplicateApply}
                                disabled={!replicateSourceDay || replicateTargetDays.length === 0}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl font-semibold text-sm transition-all ${
                                    !replicateSourceDay || replicateTargetDays.length === 0
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:shadow-lg hover:scale-105 cursor-pointer'
                                }`}
                            >
                                <Copy className="w-4 h-4" />
                                {t('availabilitytab.replicateApplyButton')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modalIsOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="availability-modal-title"
                    id="availability-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="availability-modal-title"
                                data-testid="modal-title"
                            >
                                {modalType === 'break'
                                    ? t('availabilitytab.modal_title_break_time')
                                    : modalType === 'start'
                                        ? t('availabilitytab.modal_title_start_time')
                                        : t('availabilitytab.modal_title_end_time')}
                            </h2>
                            <button
                                id="availability-modal-close-button"
                                onClick={closeModal}
                                aria-label={t('availabilitytab.button_close')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                disabled={isSubmitting}
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="availability-form" onSubmit={submitTime} data-testid="availability-form" className="space-y-4">
                                {modalType === 'break' ? (
                                    <>
                                        <div className="mb-4">
                                            <label
                                                className="block text-sm font-semibold text-gray-700 mb-2"
                                                htmlFor="break-start-hours"
                                                id="break-start-label"
                                                data-testid="break-start-label"
                                            >
                                                {t('availabilitytab.label_break_start_time')} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="grid grid-cols-2 gap-4">
                                                <CustomSelect
                                                    id="break-start-hours"
                                                    value={tempBreakStart.hours}
                                                    onChange={(value) => {
                                                        setTempBreakStart({ ...tempBreakStart, hours: value });
                                                        if (errors[timeIndex]?.breakStart) {
                                                            setErrors(prev => ({
                                                                ...prev,
                                                                [timeIndex]: { ...prev[timeIndex], breakStart: '' }
                                                            }));
                                                        }
                                                    }}
                                                    options={getFilteredHourOptions(availability[timeIndex]?.start, availability[timeIndex]?.end).map(h => ({ value: h, label: h }))}
                                                    disabled={isSubmitting || !availability[timeIndex]?.start}
                                                    error={!!errors[timeIndex]?.breakStart}
                                                    isOpen={openDropdown === 'modal-break-start-hours'}
                                                    onToggle={(isOpen) => setOpenDropdown(isOpen ? 'modal-break-start-hours' : null)}
                                                    testId="break-start-hours"
                                                    usePortal={true}
                                                />
                                                <CustomSelect
                                                    id="break-start-minutes"
                                                    value={tempBreakStart.minutes}
                                                    onChange={(value) => {
                                                        setTempBreakStart({ ...tempBreakStart, minutes: value });
                                                        if (errors[timeIndex]?.breakStart) {
                                                            setErrors(prev => ({
                                                                ...prev,
                                                                [timeIndex]: { ...prev[timeIndex], breakStart: '' }
                                                            }));
                                                        }
                                                    }}
                                                    options={getFilteredMinuteOptions(tempBreakStart.hours, availability[timeIndex]?.start, availability[timeIndex]?.end).map(m => ({ value: m, label: m }))}
                                                    placeholder="--"
                                                    disabled={isSubmitting || !availability[timeIndex]?.start}
                                                    error={!!errors[timeIndex]?.breakStart}
                                                    isOpen={openDropdown === 'modal-break-start-minutes'}
                                                    onToggle={(isOpen) => setOpenDropdown(isOpen ? 'modal-break-start-minutes' : null)}
                                                    testId="break-start-minutes"
                                                    usePortal={true}
                                                />
                                            </div>
                                            {errors[timeIndex]?.breakStart && (
                                                <p
                                                    className="mt-2 text-xs text-red-500 font-medium"
                                                    id="break-start-error"
                                                    data-testid="break-start-error"
                                                >
                                                    {errors[timeIndex].breakStart}
                                                </p>
                                            )}
                                        </div>
                                        <div className="mb-4">
                                            <label
                                                className="block text-sm font-semibold text-gray-700 mb-2"
                                                htmlFor="break-end-hours"
                                                id="break-end-label"
                                                data-testid="break-end-label"
                                            >
                                                {t('availabilitytab.label_break_end_time')} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="grid grid-cols-2 gap-4">
                                                <CustomSelect
                                                    id="break-end-hours"
                                                    value={tempBreakEnd.hours}
                                                    onChange={(value) => {
                                                        setTempBreakEnd({ ...tempBreakEnd, hours: value });
                                                        if (errors[timeIndex]?.breakEnd) {
                                                            setErrors(prev => ({
                                                                ...prev,
                                                                [timeIndex]: { ...prev[timeIndex], breakEnd: '' }
                                                            }));
                                                        }
                                                    }}
                                                    options={getFilteredHourOptions(timeAddMinutes(`${tempBreakStart.hours}:${tempBreakStart.minutes}`, 15), availability[timeIndex]?.end).map(h => ({ value: h, label: h }))}
                                                    disabled={isSubmitting || !availability[timeIndex]?.start}
                                                    error={!!errors[timeIndex]?.breakEnd}
                                                    isOpen={openDropdown === 'modal-break-end-hours'}
                                                    onToggle={(isOpen) => setOpenDropdown(isOpen ? 'modal-break-end-hours' : null)}
                                                    testId="break-end-hours"
                                                    usePortal={true}
                                                />
                                                <CustomSelect
                                                    id="break-end-minutes"
                                                    value={tempBreakEnd.minutes}
                                                    onChange={(value) => {
                                                        setTempBreakEnd({ ...tempBreakEnd, minutes: value });
                                                        if (errors[timeIndex]?.breakEnd) {
                                                            setErrors(prev => ({
                                                                ...prev,
                                                                [timeIndex]: { ...prev[timeIndex], breakEnd: '' }
                                                            }));
                                                        }
                                                    }}
                                                    options={getFilteredMinuteOptions(tempBreakEnd.hours, timeAddMinutes(`${tempBreakStart.hours}:${tempBreakStart.minutes}`, 15), availability[timeIndex]?.end).map(m => ({ value: m, label: m }))}
                                                    placeholder="--"
                                                    disabled={isSubmitting || !availability[timeIndex]?.start}
                                                    error={!!errors[timeIndex]?.breakEnd}
                                                    isOpen={openDropdown === 'modal-break-end-minutes'}
                                                    onToggle={(isOpen) => setOpenDropdown(isOpen ? 'modal-break-end-minutes' : null)}
                                                    testId="break-end-minutes"
                                                    usePortal={true}
                                                />
                                            </div>
                                            {errors[timeIndex]?.breakEnd && (
                                                <p
                                                    className="mt-2 text-xs text-red-500 font-medium"
                                                    id="break-end-error"
                                                    data-testid="break-end-error"
                                                >
                                                    {errors[timeIndex].breakEnd}
                                                </p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="mb-4">
                                            <label
                                                className="block text-sm font-semibold text-gray-700 mb-2"
                                                htmlFor="time-hours"
                                                id="time-hours-label"
                                                data-testid="time-hours-label"
                                            >
                                                {t('availabilitytab.label_hours')} <span className="text-red-500">*</span>
                                            </label>
                                            <CustomSelect
                                                id="time-hours"
                                                value={tempTime.hours}
                                                onChange={(value) => {
                                                    setTempTime({ ...tempTime, hours: value });
                                                    if (errors[timeIndex]?.[modalType]) {
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            [timeIndex]: { ...prev[timeIndex], [modalType]: '' }
                                                        }));
                                                    }
                                                }}
                                                options={getFilteredHourOptions(
                                                    modalType === 'end'
                                                        ? (availability[timeIndex]?.start ? timeAddMinutes(availability[timeIndex].start, 240) : '00:00')
                                                        : '00:00',
                                                    '23:59'
                                                ).map(h => ({ value: h, label: h }))}
                                                disabled={isSubmitting}
                                                error={!!errors[timeIndex]?.[modalType]}
                                                isOpen={openDropdown === 'modal-time-hours'}
                                                onToggle={(isOpen) => setOpenDropdown(isOpen ? 'modal-time-hours' : null)}
                                                testId="time-hours"
                                                usePortal={true}
                                            />
                                        </div>
                                        <div className="mb-4">
                                            <label
                                                className="block text-sm font-semibold text-gray-700 mb-2"
                                                htmlFor="time-minutes"
                                                id="time-minutes-label"
                                                data-testid="time-minutes-label"
                                            >
                                                {t('availabilitytab.label_minutes')} <span className="text-red-500">*</span>
                                            </label>
                                            <CustomSelect
                                                id="time-minutes"
                                                value={tempTime.minutes}
                                                onChange={(value) => {
                                                    setTempTime({ ...tempTime, minutes: value });
                                                    if (errors[timeIndex]?.[modalType]) {
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            [timeIndex]: { ...prev[timeIndex], [modalType]: '' }
                                                        }));
                                                    }
                                                }}
                                                options={getFilteredMinuteOptions(
                                                    tempTime.hours,
                                                    modalType === 'end'
                                                        ? (availability[timeIndex]?.start ? timeAddMinutes(availability[timeIndex].start, 240) : '00:00')
                                                        : '00:00',
                                                    '23:59'
                                                ).map(m => ({ value: m, label: m }))}
                                                placeholder="--"
                                                disabled={isSubmitting}
                                                error={!!errors[timeIndex]?.[modalType]}
                                                isOpen={openDropdown === 'modal-time-minutes'}
                                                onToggle={(isOpen) => setOpenDropdown(isOpen ? 'modal-time-minutes' : null)}
                                                testId="time-minutes"
                                                usePortal={true}
                                            />
                                        </div>
                                        {errors[timeIndex]?.[modalType] && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium col-span-2"
                                                id="time-error"
                                                data-testid="time-error"
                                            >
                                                {errors[timeIndex][modalType]}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="availability-form-cancel-button"
                                onClick={closeModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isSubmitting}
                                data-testid="cancel-button"
                            >
                                {t('availabilitytab.button_close')}
                            </button>
                            <button
                                type="submit"
                                id="availability-form-submit-button"
                                form="availability-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                                disabled={isSubmitting}
                                data-testid="submit-button"
                            >
                                {isSubmitting && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {t('availabilitytab.button_save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <Tooltip id="warningTooltip" place="top" effect="solid" backgroundColor="#f39c12" />
        </div>
    );
};

export default AvailabilityTab;